import { useState, useEffect, useCallback } from "react";
import { FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "./adminApi";

const APPLICATION_STATUSES = [
  { key: "pending", label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "reviewed", label: "Consultée", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  { key: "accepted", label: "Acceptée", cls: "bg-green-100 text-green-800 border-green-200" },
  { key: "rejected", label: "Refusée", cls: "bg-red-100 text-red-800 border-red-200" },
];

export default function ApplicationsTab({ onOpenCandidate, initialFilter }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Optional filter on a specific offer — set by clicking the "X candidatures"
  // badge on an offer card. Cleared by the "x" button next to the chip.
  const [offerFilter, setOfferFilter] = useState(null);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/applications");
      setApps(r.data);
    } catch {
      toast.error("Impossible de charger les candidatures");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // React to filter pushed from OffersTab when user clicks the count badge
  useEffect(() => {
    if (initialFilter?.filters?.offer_id) {
      setOfferFilter({
        offer_id: initialFilter.filters.offer_id,
        offer_title: initialFilter.filters.offer_title,
        offer_number: initialFilter.filters.offer_number,
      });
      setStatusFilter("all");
      setSearch("");
    }
  }, [initialFilter]);

  const updateStatus = async (app, newStatus) => {
    try {
      await api.patch(`/applications/${app.application_id}/status`, { status: newStatus });
      setApps((prev) => prev.map((a) => a.application_id === app.application_id ? { ...a, status: newStatus } : a));
      toast.success(`Statut mis à jour : ${APPLICATION_STATUSES.find((s) => s.key === newStatus)?.label}`);
    } catch {
      toast.error("Impossible de mettre à jour le statut");
    }
  };

  const filtered = apps.filter((a) => {
    if (offerFilter?.offer_id && a.offer_id !== offerFilter.offer_id) return false;
    if (statusFilter !== "all" && (a.status || "pending") !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (a.user_name || "").toLowerCase().includes(s)
        || (a.user_email || "").toLowerCase().includes(s)
        || (a.user_phone || "").toLowerCase().includes(s)
        || (a.offer_title || "").toLowerCase().includes(s)
        || (a.offer_number || "").toLowerCase().includes(s);
    }
    return true;
  });

  const counts = APPLICATION_STATUSES.reduce((acc, s) => {
    const base = offerFilter?.offer_id ? apps.filter((a) => a.offer_id === offerFilter.offer_id) : apps;
    acc[s.key] = base.filter((a) => (a.status || "pending") === s.key).length;
    return acc;
  }, { all: (offerFilter?.offer_id ? apps.filter((a) => a.offer_id === offerFilter.offer_id) : apps).length });

  return (
    <div data-testid="applications-tab" className="space-y-6">
      {offerFilter?.offer_id && (
        <div className="bg-[#F0E9F3] border border-[#662483]/30 px-3 py-2 flex items-center gap-2 text-xs" data-testid="apps-offer-filter-chip">
          <span className="text-gray-600">Filtrage actif :</span>
          <strong className="text-[#662483]">{offerFilter.offer_number || ""} {offerFilter.offer_title || ""}</strong>
          <button
            type="button"
            onClick={() => setOfferFilter(null)}
            className="ml-auto text-[#662483] hover:bg-[#662483] hover:text-white px-2 py-0.5 transition-colors"
            data-testid="apps-clear-offer-filter"
          >
            ✕ Voir toutes les candidatures
          </button>
        </div>
      )}
      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter("all")}
          className={`px-4 py-2 text-sm font-semibold border transition-colors ${statusFilter === "all" ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`}
          data-testid="filter-status-all"
        >
          Toutes <span className="ml-1 opacity-75">({counts.all})</span>
        </button>
        {APPLICATION_STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            className={`px-4 py-2 text-sm font-semibold border transition-colors ${statusFilter === s.key ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`}
            data-testid={`filter-status-${s.key}`}
          >
            {s.label} <span className="ml-1 opacity-75">({counts[s.key] || 0})</span>
          </button>
        ))}
        <div className="flex-1 min-w-[220px]">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par candidat, offre, référence..."
            className="rounded-none"
            data-testid="applications-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">Aucune candidature {statusFilter !== "all" ? `avec le statut "${APPLICATION_STATUSES.find((s) => s.key === statusFilter)?.label}"` : ""}.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 divide-y divide-gray-100">
          {filtered.map((a) => {
            const status = a.status || "pending";
            const statusDef = APPLICATION_STATUSES.find((s) => s.key === status);
            return (
              <div key={a.application_id} data-testid={`admin-application-${a.application_id}`} className="p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-bold text-[#662483]">{a.offer_number || "—"}</span>
                    <Badge className={`${statusDef?.cls || ""} rounded-none text-[10px] font-bold uppercase tracking-wider border`}>{statusDef?.label || status}</Badge>
                    <span className="text-[11px] text-gray-400">{new Date(a.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#3F3F3F]">{a.offer_title || "(Offre supprimée)"}</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    <button
                      type="button"
                      onClick={() => a.user_id && onOpenCandidate?.(a.user_id)}
                      className="font-medium text-[#0069B4] hover:underline hover:text-[#662483] transition-colors"
                      data-testid={`open-candidate-${a.application_id}`}
                      disabled={!a.user_id}
                    >
                      {a.user_name || "(sans nom)"}
                    </button>
                    <span className="text-gray-400"> • </span>
                    <a href={`mailto:${a.user_email}`} className="hover:underline">{a.user_email}</a>
                    {a.user_phone && (
                      <>
                        <span className="text-gray-400"> • </span>
                        <a href={`tel:${a.user_phone.replace(/\s/g, "")}`} className="hover:underline" data-testid={`app-phone-${a.application_id}`}>{a.user_phone}</a>
                      </>
                    )}
                  </p>
                  {a.comment && (
                    <p className="text-xs text-gray-600 mt-2 italic border-l-2 border-[#662483]/30 pl-2 max-w-2xl">
                      « {a.comment} »
                    </p>
                  )}
                </div>
                <div className="w-full lg:w-auto flex-shrink-0">
                  <Select value={status} onValueChange={(v) => updateStatus(a, v)}>
                    <SelectTrigger className="w-full lg:w-[180px] rounded-none" data-testid={`status-select-${a.application_id}`}>
                      <SelectValue placeholder="Changer le statut" />
                    </SelectTrigger>
                    <SelectContent>
                      {APPLICATION_STATUSES.map((s) => (
                        <SelectItem key={s.key} value={s.key} data-testid={`status-option-${s.key}`}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
